/**
 * Aligned ERP sidebar (13 modules).
 * enabled:false = coming-soon stub (shown only to Admin/Ops roles 1 & 3).
 * roles: string role IDs that may see an enabled item.
 * rolesExclude: if set, visible to everyone except those roles (e.g. Employee Dashboard).
 * openInNewTab: use <a href> instead of router Link.
 */

export const ADMIN_OPS_ROLES = ["1", "3"];

const stub = (label) => ({ label, enabled: false });

const link = (label, path, roles, extra = {}) => ({
  label,
  path,
  enabled: true,
  roles,
  ...extra,
});

const group = (label, children) => ({
  kind: "group",
  label,
  children,
});

const sectionLabel = (label) => ({
  kind: "label",
  label,
});

/** @type {Array<{id: string, label: string, icon?: string, children: Array}>} */
export const ALIGNED_MENU = [
  // ─── 01 DASHBOARD ───────────────────────────────────────────
  {
    id: "dashboard",
    label: "Dashboard",
    icon: "bi-house-fill",
    children: [
      link("Admin Dashboard", "/admin-dashboard", ["1"]),
      link("Reminders", "/admin-reminders", ["1"]),
      link("Cafe Dashboard", "/cafe-overview", ["1", "3"]),
      link("Role & Menu Access", "/admin/role-access", ["1"]),
      link("Nutrition Dashboard", "/nutrition/dashboard", ["1", "3", "10", "11"]),
      link("Fitness Dashboard", "/fitness-manager-dashboard", ["1", "11"]),
      link("PT Dashboard", "/pt-dashboard", ["1", "3", "9", "11"]),
      link("GT Dashboard", "/gt-dashboard", ["1", "3", "11", "17"]),
      link("Social Media Dashboard", "/social-media-dashboard", ["1", "3", "16"]),
      link("Dashboard", "/dashboard", ["1", "3", "4"]),
      stub("My Dashboard"),
      link("Employee Dashboard", "/employee-dashboard", null, {
        rolesExclude: ["1", "3", "11", "16"],
      }),
      link("Announcements", "/announcements", ["1", "11", "12"]),
      link("Approvals", "/approval", ["1", "3", "4"]),
      stub("Alerts & Notifications"),
      stub("Calendar"),
    ],
  },

  // ─── 02 CRM / CLIENTS ───────────────────────────────────────
  {
    id: "crm",
    label: "CRM / Clients",
    icon: "bi-people-fill",
    children: [
      link("Clients", "/view-clients", ["1", "3", "4"]),
      link("Add Client", "/add-clients", ["1", "3", "4"]),
      link("Memberships", "/membership-package", ["1", "3", "4"]),
      group("Packages", [
        link("Membership Packages", "/membership-package", ["1", "3", "4"]),
        link("Gym Packages", "/gym-package", ["1", "3", "4"]),
        link("Trainer Packages", "/trainer-package", ["1", "3", "4"]),
        link("Bootcamp Packages", "/add-bootcamp-calsses", ["1", "3", "4"]),
        link("Physiotherapy Packages", "/physiotherapy-package", ["1", "3", "4"]),
        link("Massage Chair", "/massagechair", ["1", "3", "4"]),
        link("Small PT Group Packages", "/add-group", ["1", "3", "4"]),
        link("GX Packages", "/add-gx-package", ["1", "3", "4"]),
        link("CFT", "/cft-package", ["1", "3", "4"]),
        link("General Packages", "/genral-package", ["1", "3", "4"]),
        link("Detailed Packages", "/detailed-packages", ["1", "3", "4"]),
      ]),
      link("Freezing", "/freezing-management", ["1", "3", "4"]),
      link("Access Control — Assign Cards", "/assign-cards", ["1", "3", "4"]),
      link("Access Control — View Cards", "/view-cards", ["1", "3", "4"]),
      stub("Client Profile"),
      stub("Client Attendance"),
      stub("Client Notes"),
      stub("Client Documents"),
    ],
  },

  // ─── 03 SALES ───────────────────────────────────────────────
  {
    id: "sales",
    label: "Sales",
    icon: "bi-cart-check-fill",
    children: [
      stub("Sales Dashboard"),
      stub("Leads"),
      link("Social Leads", "/social-leads-sales", ["1", "3", "4"]),
      link("New Sales", "/search-client", ["1", "3", "4"]),
      stub("Renewals"),
      link("Package Sales", "/search-client", ["1", "3", "4"]),
      link("Payments / Cards", "/view-cards", ["1", "3", "4"]),
      link("Categories", "/manage-categories", ["1", "3", "5"]),
      link("Sub-Categories", "/sub-categories", ["1", "3", "5"]),
      link("Slots Management (Trainers)", "/trainer-slots", ["1", "3", "4"]),
      link("Daily Sales Report", "/daily-sales-counter", ["1", "3", "4"]),
      link("Clients Report", "/clients-reports", ["1", "3", "4"]),
      stub("Sales Reports"),
    ],
  },

  // ─── 04 FITNESS ─────────────────────────────────────────────
  {
    id: "fitness",
    label: "Fitness",
    icon: "bi-heart-pulse-fill",
    homePath: "/fitness-manager-dashboard",
    children: [
      link("Fitness Dashboard", "/fitness-manager-dashboard", ["1", "3", "11"]),
      link("PT Dashboard", "/pt-dashboard", ["1", "3", "9", "11"]),
      link("Slots Management", "/trainer-slots", ["1", "9", "11"]),
      link("GT Dashboard", "/gt-dashboard", ["1", "3", "11", "17"]),
      link("Session Tracker", "/session-tracker", ["1", "3", "9", "11"]),
      link("SOPs", "/sops", ["1", "9", "11"]),
      link("Trainer Diary", "/trainer-diary", ["1", "3", "11"]),
      link("Personal Trainer Diary", "/personal-trainer", ["1", "3", "4", "9", "11"]),
      group("Trainer Schedule", [
        link("Time Slots", "/time-slot", ["1", "3", "11"]),
        link("Manage Availability", "/assign-time-slot", ["1", "3", "11"]),
      ]),
      link("PT Bookings", "/new-pt-bookings", ["1", "3", "11"]),
      link("New PT Clients", "/new-pt-packages", ["1", "3", "11"]),
      link("Trainer Appointments", "/trainer-appointments", ["1", "3", "11"]),
      link("Switch Booking Time", "/switch-time", ["1", "3", "11"]),
      link("PT Sales", "/pt-sales-report", ["1", "3", "11"]),
      stub("Session Management"),
      group("GX Classes", [
        link("Add Class", "/add-gx-classes", ["1", "3", "10", "11"]),
        link("GX Slots List", "/gx-classes", ["1", "3", "10", "11"]),
        link("GX Appointments", "/gx-appointments", ["1", "3", "10", "11"]),
        link("GX Attendance", "/gx-classes-attendance", ["1", "3", "10", "11"]),
        link("GX Attendance Report", "/gx-attendance-report", ["1", "3", "10", "11"]),
      ]),
      group("BeFit", [
        link("Befit List", "/befit-list", ["1", "3", "11"]),
        link("Befit Bookings", "/new-befit-bookings", ["1", "3", "11"]),
        link("Befit Appointments", "/befit-appointments", ["1", "3", "11"]),
        link("Befit Attendance", "/befit-attendance", ["1", "3", "11"]),
        link("Befit Attendance Report", "/befit-attendance-report", ["1", "3", "11"]),
      ]),
      group("SPT", [
        link("SPT List", "/spt-list", ["1", "3", "11"]),
        link("SPT Bookings", "/new-spt-bookings", ["1", "3", "11"]),
        link("SPT Appointments", "/spt-appointments", ["1", "3", "11"]),
        link("SPT Attendance", "/spt-attendance", ["1", "3", "11"]),
        link("SPT Attendance Report", "/spt-attendance-report", ["1", "3", "11"]),
      ]),
      group("Fitness Plans", [
        link("View Plans", "/fitness-plan", ["1", "3", "9", "11"]),
        link("Add Plans", "/add-fitness-plan", ["1", "3", "9", "11"]),
        link("Manage Exercises", "/manage-exercises", ["1", "3", "9", "11"]),
      ]),
      stub("Fitness Reports"),
    ],
  },

  // ─── 05 HR ──────────────────────────────────────────────────
  {
    id: "hr",
    label: "HR",
    icon: "bi-person-badge-fill",
    homePath: "/hr-dashboard",
    children: [
      link("HR Dashboard", "/hr-dashboard", ["1", "3", "12"]),
      link("Employee Master", "/view-staff", ["1", "3", "12"]),
      link("SOPs", "/sops", ["1", "9", "11", "12"]),
      stub("Employee Profile"),
      link("Attendance", "/staff-attendance", ["1", "3", "12"]),
      link("Employee Attendance", "/employee-attendance", ["1", "3", "12"]),
      link("PT Attendance", "/pt-attendance", ["1", "3", "12"]),
      link("Staff Duty Hours", "/staff-duty-hours", ["1", "3", "12"]),
      group("Leave Management", [
        link("Leave Quota", "/leave-quota", ["1", "3", "12"]),
        link("Leave Application", "/leave-application", ["1", "3", "12"]),
      ]),
      link("Salary Management", "/staff-salary", ["1", "3", "12"]),
      link("Salary Components", "/salary-components", ["1", "3", "12"]),
      link("Advances, Loans & Fines", "/staff-loans", ["1", "3", "12"]),
      link("Promotions & Disciplinary", "/add-staff-promotion", ["1", "3", "12"]),
      link("Session Portal", "/hr-session-portal", ["1", "3", "11", "12"]),
      link("Resource Management", "/resource-manager", ["1", "3", "12"]),
      link("Letters & Certificates", "/letter-managemnt", ["1", "3", "12"]),
      link("HR Reports", "/detailed-hr-report", ["1", "3", "12"]),
    ],
  },

  // ─── 06 FINANCE ─────────────────────────────────────────────
  {
    id: "finance",
    label: "Finance",
    icon: "bi-cash-coin",
    children: [
      sectionLabel("Finance V2 Books"),
      link("Finance Dashboard", "/finance-v2/dashboard", ["1", "3", "5"]),
      link("Setup wizard", "/finance-v2/setup", ["1", "3", "5"]),
      link("Import & sync", "/finance-v2/import", ["1", "3", "5"]),
      link("Journals", "/finance-v2/journals", ["1", "3", "5"]),
      link("Financial Reports (V2)", "/finance-v2/reports", ["1", "3", "5"]),
      stub("Accounts"),
      stub("Receivables"),
      stub("Payables"),
      sectionLabel("Legacy finance"),
      link("Finance dashboard (legacy)", "/finance-dashboard", ["1", "3", "5"]),
      group("Expenses", [
        link("Add Expense", "/add-expenses", ["1", "3", "5"]),
        link("View Expenses", "/view-expenses", ["1", "3", "5"]),
        link("Daily Expense", "/vostro-expense", ["1", "3", "5"]),
        link("Daily Expense Report", "/vostro-expense-report", ["1", "3", "5"]),
        link("Paid Expense Report", "/paid-expense-report", ["1", "3", "5"]),
      ]),
      group("Cash", [
        link("Add Cash In Hand", "/cash-in-hand", ["1", "3", "5"]),
        link("View Cash In Hand", "/view-cash-in-hand", ["1", "3", "5"]),
        link("Add Office Cash", "/add-office-cash", ["1", "3", "5"]),
        link("View Office Ledger", "/view-cash-ledger", ["1", "3", "5"]),
        link("Add Petty Cash", "/add-petty-cash", ["1", "3", "5"]),
        link("Petty Cash Ledger", "/petty-cash-ledger", ["1", "3", "5"]),
        link("Add G13 Cash", "/add-branch-cash", ["1", "3", "5"]),
        link("G13 Cash Ledger", "/branch-cash-ledger", ["1", "3", "5"]),
      ]),
      group("Bank", [
        link("Add Bank Cash", "/add-bank-cash", ["1", "3", "5"]),
        link("View Bank Ledger", "/bank-ledger", ["1", "3", "5"]),
        link("Bank Details", "/bank-details", ["1", "3", "5"]),
      ]),
      group("Charity", [
        link("Add Charity", "/add-charity", ["1", "3", "5"]),
        link("View Charity Ledger", "/view-charity", ["1", "3", "5"]),
      ]),
      group("Liabilities", [
        link("Add Liabilities", "/liabilities-management", ["1", "3", "5"]),
        link("Pay Liabilities", "/add-liabilities-ledger", ["1", "3", "5"]),
        link("View Liabilities Ledger", "/liabilities-ledger", ["1", "3", "5"]),
      ]),
      group("Keene Ledger", [
        link("Add Keene", "/add-keene", ["1", "3", "5"]),
        link("Keene Ledger", "/keene-ledger", ["1", "3", "5"]),
      ]),
      link("Assets", "/manage-assets", ["1", "3", "5"]),
      link("Balance Sheet", "/balance-sheet", ["1", "3", "5"]),
      link("Daily Sales Counter", "/daily-sales-counter", ["1", "3", "5"]),
      link("Cafe Sales & Expense Report", "/cafe-sales-expense-report", ["1", "3", "5"]),
      link("Daily Closing", "/daily-office-closing", ["1", "3", "5"]),
      stub("Ledgers"),
    ],
  },

  // ─── 07 NUTRITION ───────────────────────────────────────────
  {
    id: "nutrition",
    label: "Nutrition",
    icon: "bi-egg-fried",
    children: [
      link("Nutrition Dashboard", "/nutrition/dashboard", ["1", "3", "10", "11"]),
      link("Clients", "/view-clients", ["1", "3", "11"]),
      link("Clients Details", "/nutrition/clients-details", ["1", "3", "10", "11"]),
      link("Appointments", "/nutrition/appointments", ["1", "3", "4", "10", "11"]),
      group("Assessments", [
        link("Add Nutrition Assessments", "/nutrition-assessments", ["1", "3", "10", "11"]),
        link("View Nutrition Assessments", "/view-nutrition-assessments", ["1", "3", "10", "11"]),
        link("Assessment Questionnaire", "/nutritionist-assessment-form", ["1", "3", "10", "11"]),
      ]),
      group("Meal Plans", [
        link("Add Meals Plan", "/add-meal-plan", ["1", "3", "10", "11"]),
        link("View Meals Plan", "/view-meal-plan", ["1", "3", "10", "11"]),
      ]),
      link("Health Camps", "/nutrition/health-camps", ["1", "3", "10", "11"]),
      link("Referrals", "/nutrition/referrals", ["1", "3", "10", "11"]),
      stub("Client Responses"),
      link("Nutrition Packages", "/add-nutrition-package", ["1", "3", "4"]),
      link("Image Gallery", "/nutrition/gallery", ["1", "3", "10", "11"]),
      stub("Nutrition Reports"),
    ],
  },

  // ─── 08 PHYSIO ──────────────────────────────────────────────
  {
    id: "physio",
    label: "Physio",
    icon: "bi-bandaid-fill",
    children: [
      link("Physio Dashboard", "/physio/dashboard", ["1", "3", "15"]),
      link("Clients / Patient Details", "/physio/patient-details", ["1", "3", "15"]),
      link("Appointments", "/physio/appointments", ["1", "3", "4", "15"]),
      link("Assessments", "/physio/assessments", ["1", "3", "15"]),
      link("Prescriptions", "/physio/prescriptions", ["1", "3", "15"]),
      link("Sessions / GX", "/physio/gx", ["1", "3", "15"]),
      link("Referrals", "/physio/daily-referrals", ["1", "3", "15"]),
      link("Client Responses", "/physio/client-responses", ["1", "3", "15"]),
      stub("Physio Reports"),
    ],
  },

  // ─── 09 CAFE ────────────────────────────────────────────────
  {
    id: "cafe",
    label: "Cafe",
    icon: "bi-cup-hot-fill",
    children: [
      link("Cafe Dashboard", "/cafe-dashboard", ["1", "3", "4", "7"], {
        openInNewTab: true,
      }),
      link("Categories", "/add-cafe-category", ["1", "3", "7"]),
      link("Products", "/cafe", ["1", "3", "7"]),
      link("Client Deposits", "/cafe-deposit", ["1", "3", "4", "7"]),
      link("Add Clients Deposit", "/deposit-client-balance", ["1", "3", "4", "7"]),
      link("Client Balance", "/clients-available-balance", ["1", "3", "4", "7"]),
      link("Deposit History", "/deposit-history", ["1", "3", "4", "7"]),
      stub("Sales"),
      stub("Expenses"),
      stub("Closing"),
      link("Cafe Sales Report", "/cafe-sales-report", ["1", "3", "4", "7"]),
      link("Detailed Cafe Report", "/detailed-cafe-report", ["1", "3", "4", "7"]),
      link("Management Pendings", "/management-pendings", ["1", "3", "4", "7"]),
      stub("Cafe Reports"),
    ],
  },

  {
    id: "inventory",
    label: "Inventory",
    icon: "bi-box-seam",
    children: [
      link("Dashboard", "/inventory", ["1", "3", "7"]),
      link("Stock", "/inventory/stock", ["1", "3", "7"]),
      link("Receive stock", "/inventory/receive", ["1", "3", "7"]),
      link("Issue stock", "/inventory/issue", ["1", "3", "7"]),
      link("Demands", "/inventory/demands", ["1", "3", "7"]),
      link("Water bottles", "/inventory/water-bottles", ["1", "3", "7"]),
      link("Move stock", "/inventory/transfer", ["1", "3"]),
      link("Products", "/inventory/products", ["1"]),
      link("Catalog", "/inventory/catalog", ["1"]),
      link("Cafe recipes", "/inventory/recipes", ["1", "3", "7"]),
      link("Movements", "/inventory/movements", ["1", "3", "7"]),
      link("Reports", "/inventory/reports", ["1", "3", "7"]),
    ],
  },

  // ─── 10 FACILITY ────────────────────────────────────────────
  {
    id: "facility",
    label: "Facility",
    icon: "bi-building",
    children: [
      link("Asset Management", "/gym-assets", ["1", "3", "12"]),
      link("Maintenance", "/gym-maintenance", ["1", "3", "12"]),
      link("Facility Dashboard", "/facility-dashboard", ["1", "3", "12"]),
      group("Towel Management", [
        link("Towel Stock / Manage Towels", "/manage-towels", ["1", "3", "5"]),
        stub("Towel Issuance"),
        stub("Towel Return"),
        stub("Damaged / Missing"),
        stub("Towel Reports"),
      ]),
      group("Locker Management", [
        link("Locker Board", "/locker-management", ["1"]),
      ]),
    ],
  },

  // ─── 11 MARKETING ───────────────────────────────────────────
  {
    id: "marketing",
    label: "Marketing",
    icon: "bi-megaphone-fill",
    children: [
      link("Marketing Dashboard", "/social-media-dashboard", ["1", "3", "16"]),
      stub("Campaigns"),
      stub("Social Media"),
      stub("Content"),
      link("Social Leads", "/social-leads", ["1", "3", "16"]),
      stub("Campaign Leads"),
      stub("Promotions & Offers"),
      stub("Events"),
      stub("Media Library"),
      stub("Marketing Reports"),
    ],
  },

  // ─── 12 REPORTS ─────────────────────────────────────────────
  {
    id: "reports",
    label: "Reports",
    icon: "bi-bar-chart-fill",
    children: [
      group("Sales Reports", [
        link("Sales", "/sales-report", ["1", "3", "4", "5"]),
        link("Detailed Sales Report", "/detailed-sales-report", ["1", "3", "4", "5"]),
        link("Daily Report", "/daily-report", ["1", "3", "5"]),
        link("MIS Report", "/mis-report", ["1"]),
        link("Sales By Services", "/sales-by-services", ["1", "3", "4", "5"]),
        link("Sales & Expense Daily", "/sales-expense-daily", ["1", "3", "5"]),
        link("Sales By Bootcamp", "/sales-by-bootcamp", ["1", "3", "4", "5"]),
        link("Transaction Report", "/transactions-report", ["1", "3", "4", "5"]),
      ]),
      group("Client Reports", [
        link("Clients Reports", "/clients-reports", ["1", "3", "4"]),
        link("Active Clients Report", "/active-clients-report", ["1", "3", "4", "5"]),
        link("Client Details Report", "/client-details-report", ["1", "3", "4", "5"]),
        link("Clients Attendance", "/attendance-report", ["3", "4"]),
        link("Footfall Report", "/footfall-report", ["3", "4"]),
      ]),
      group("Cafe Reports", [
        link("Cafe Sales", "/cafe-sales-report", ["1", "3", "4", "5"]),
        link("Detailed Cafe Report", "/detailed-cafe-report", ["1", "3", "4", "5"]),
      ]),
      group("Fitness Reports", [
        link("PT Sales Report", "/pt-sales-report", ["1", "3", "11"]),
        stub("Fitness Reports Hub"),
      ]),
      group("HR Reports", [
        link("Detailed HR Report", "/detailed-hr-report", ["1", "3", "12"]),
      ]),
      group("Finance Reports", [
        link("Financial Reports (V2)", "/finance-v2/reports", ["1", "3", "5"]),
        link("Cafe Sales & Expense Report", "/cafe-sales-expense-report", ["1", "3", "5"]),
      ]),
      stub("Nutrition Reports"),
      stub("Physio Reports"),
      stub("Facility Reports"),
      stub("Marketing Reports"),
    ],
  },

  // ─── 13 ADMINISTRATION ──────────────────────────────────────
  {
    id: "administration",
    label: "Administration",
    icon: "bi-gear-fill",
    children: [
      stub("Users & Roles"),
      stub("Access Control"),
      link("Role & Menu Access", "/admin/role-access", ["1"]),
      link("Branch Management", "/manage-branches", ["1", "3"]),
      link("Settings", "/settings", ["1", "3", "5"]),
      link("Approval Center", "/approval", ["1", "3", "4"]),
      stub("Activity Log"),
      stub("System Backup"),
    ],
  },
];

export function isAdminOps(role) {
  return ADMIN_OPS_ROLES.includes(String(role ?? ""));
}

export function canSeeMenuItem(item, role) {
  const r = String(role ?? "");
  if (item.kind === "label") return isAdminOps(r); // section labels only useful for admins browsing finance
  if (item.kind === "group") {
    return (item.children || []).some((c) => canSeeMenuItem(c, r));
  }
  if (item.enabled === false) {
    return isAdminOps(r);
  }
  if (item.rolesExclude && item.rolesExclude.map(String).includes(r)) {
    return false;
  }
  if (!item.roles || item.roles.length === 0) {
    return true;
  }
  return item.roles.map(String).includes(r);
}

/** Finance section labels should show if any sibling finance content is visible. */
export function canSeeLabelInModule(module, role) {
  return (module.children || []).some(
    (c) => c.kind !== "label" && canSeeMenuItem(c, role)
  );
}

export function filterModuleForRole(module, role) {
  const r = String(role ?? "");
  const filterChildren = (children) =>
    (children || [])
      .map((child) => {
        if (child.kind === "label") {
          return canSeeLabelInModule(module, r) ? child : null;
        }
        if (child.kind === "group") {
          const next = filterChildren(child.children);
          if (!next.length) return null;
          return { ...child, children: next };
        }
        return canSeeMenuItem(child, r) ? child : null;
      })
      .filter(Boolean);

  const children = filterChildren(module.children);
  if (!children.length) return null;
  return { ...module, children };
}

export function getVisibleModules(role) {
  return ALIGNED_MENU.map((m) => filterModuleForRole(m, role)).filter(Boolean);
}

export function collectPaths(nodes, acc = []) {
  (nodes || []).forEach((n) => {
    if (n.path) acc.push(n.path);
    if (n.children) collectPaths(n.children, acc);
  });
  return acc;
}

export function pathMatches(pathname, path) {
  if (!path) return false;
  if (pathname === path) return true;
  // finance-v2 nested
  if (path.startsWith("/finance-v2") && pathname.startsWith(path)) return true;
  return false;
}

export function isTreeActive(nodes, pathname) {
  return collectPaths(nodes).some((p) => pathMatches(pathname, p));
}
